import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useParams } from 'react-router-dom';
import moment from 'moment-timezone';

import fetchSalon from 'actions/fetchSalon.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { LT_ROUTE } from 'bl/loggers.bl';
import SENTRY from 'config/sentry.singleton';
import TOKEN_REFRESHER_TIMER from 'config/trt.singleton';
import { selectCurrentSalon, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { refreshCognitoUserToken, selectCognito } from 'utils/cognito.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import PrivateRoute from 'lib/PrivateRoute';

const CTAG = 'SalonLoader';
const L = logger([CTAG, LT_ROUTE]);

const USE_UPDATED_MOMENT_LOCALE = false;

const LoadedSalonRoute = (props) => {
  const { salonId } = props;

  // SEE: https://getslick.atlassian.net/browse/GS-13377
  // SEE: https://www.npmjs.com/package/react-datepicker/v/1.8.0
  // SEE: https://momentjs.com/docs/#/customization/dow-doy/
  if (USE_UPDATED_MOMENT_LOCALE) moment.locale('en-GB');

  // use memo - memo executes right away, effect only after render
  useMemo(() => void SENTRY.salon({ salonId, tag: CTAG }), [salonId]);

  return <Route {...props} />;
};

const SalonLoader = (props) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const currentSalonId = useSelector(selectCurrentSalonId);
  const currentSalon = useSelector(selectCurrentSalon);
  const cognito = useSelector(selectCognito);

  const prevCognitoRef = useRef(cognito);
  const refetchRef = useRef(null);
  const intervalIdRef = useRef(null);

  const doFetchSalon = useCallback((salonId) => dispatch(fetchSalon(salonId)), [dispatch]);
  const doGetStylists = useCallback((salonId) => dispatch(fetchStylistsBySalon(salonId)), [dispatch]);

  const setRefreshTimer = useCallback(
    (cognitoValue) => {
      const { userPool, user } = asObject(cognitoValue);
      const { username, signInUserSession } = asObject(user);

      if (!user || !signInUserSession) return;

      // We've got a new refresh token!

      const { refreshToken, idToken } = signInUserSession;
      const beforeExpiryTime = idToken.getExpiration() - 5 * 60;

      let timeToRefresh = beforeExpiryTime * 1000 - new Date();
      if (timeToRefresh < 0) timeToRefresh = 0;

      clearTimeout(TOKEN_REFRESHER_TIMER.get());
      TOKEN_REFRESHER_TIMER.set(
        setTimeout(
          () =>
            refreshCognitoUserToken({
              userPool,
              userName: username,
              refreshToken: refreshToken.getToken(),
              dispatch,
            }),
          timeToRefresh,
        ),
      );
    },
    [dispatch],
  );

  const refetchSalonDetails = useCallback(() => {
    if (id === `${currentSalonId}`) {
      clearInterval(intervalIdRef.current);
      return;
    }

    if (currentSalon?.isFetching || currentSalon?.error) return;

    L.unignore('refetchSalonDetails()', 'fetchSalon:', doFetchSalon(parseInt(id, 10)));
  }, [id, currentSalonId, currentSalon, doFetchSalon]);

  // Keep refetchRef in sync so the interval closure always calls the latest version
  refetchRef.current = refetchSalonDetails;

  // Start polling interval on mount, cleanup on unmount
  useEffect(() => {
    intervalIdRef.current = setInterval(() => refetchRef.current(), 1000);
    if (!currentSalonId) refetchSalonDetails();

    return () => clearInterval(intervalIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UNSAFE_componentWillReceiveProps — token refresh
  useEffect(() => {
    const prevCognito = prevCognitoRef.current;
    prevCognitoRef.current = cognito;

    const prevUser = prevCognito?.user;
    const nextUser = cognito?.user;

    const prevToken = prevUser?.signInUserSession?.refreshToken?.token;
    const nextToken = nextUser?.signInUserSession?.refreshToken?.token;

    if (nextUser && prevUser && nextToken !== prevToken) setRefreshTimer(cognito);
  }, [cognito, setRefreshTimer]);

  // UNSAFE_componentWillReceiveProps — salon id change
  useEffect(() => {
    const newSalonId = parseInt(id, 10);

    if (currentSalonId && currentSalonId !== newSalonId) {
      L.unignore('useEffect[currentSalonId]', 'fetchSalon:', doFetchSalon(newSalonId));
      L.unignore('useEffect[currentSalonId]', 'getStylists:', doGetStylists(newSalonId));
    }
  }, [currentSalonId, id, doFetchSalon, doGetStylists]);

  const allProps = { ...asObject(props), currentSalonId, currentSalon };

  if (id === `${currentSalonId}`) {
    return <LoadedSalonRoute {...allProps} salonId={currentSalonId} />;
  }

  const { isFetching, error } = currentSalon ?? {};

  return (
    <div data-bem="SalonLoader" className="loading-wrap">
      <div data-bem="SalonLoader__animation" className="loading-anim">
        <div data-bem="SalonLoader__title" className="loading-title">
          <span data-bem="SalonLoader__text">Loading Salon Data...</span>
        </div>
      </div>

      {fromAnyToBool(error) && <div data-bem="SalonLoader__error" className="loading-feedback">{`${error?.code}: ${error?.message}`}</div>}

      {fromAnyToBool(isFetching) && (
        <div data-bem="SalonLoader__fetch" className="loading-feedback">
          Checking Salon Details
        </div>
      )}

      {fromAnyToBool(!isFetching && !error) && (
        <div data-bem="SalonLoader__normal" className="loading-feedback">
          Twiddling digital thumbs
        </div>
      )}
    </div>
  );
};

const SalonRoute = ({ component, ...rest }) => <PrivateRoute {...rest} component={SalonLoader} componentToLoad={component} />;

export default SalonRoute;
